'use client'

import { usePathname } from 'next/navigation'
import { Minus, Plus, Trash2, Heart, Gift, CirclePercent, FileDown, Medal } from 'lucide-react'
import { useCartItem } from '@/lib/commerce/hooks'
import { formatPrice } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'

export default function CartCard({ product }: { product: any }) {
  const pathname = usePathname() || ''
  const c = useCartItem(product)

  return (
    <div className="grid grid-cols-[88px_1fr_auto] gap-4.5 py-5 border-b border-foreground/10 last:border-0 items-center">
      <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className="relative shrink-0 block w-22 h-22">
        <div className="w-22 h-22 rounded-2xl overflow-hidden bg-[#F1EEE7]">
          <img src={c.imageUrl} alt={c.productName} className="w-full h-full object-cover" />
        </div>
        {c.isReward && (
          <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#FF7AA8] text-white inline-flex items-center justify-center shadow-sm ring-2 ring-white z-10">
            <Gift className="size-3.5" strokeWidth={2.5} />
          </span>
        )}
      </a>

      <div className="min-w-0">
        <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className="block">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/55 mb-1">{product?.brand?.name || ''}</div>
          <div className="text-[16px] sm:text-[18px] font-semibold font-gilroy tracking-[-0.01em] leading-[1.3] text-foreground line-clamp-2">{c.productName}</div>
        </a>
        {product?.isDigital && (
          <span className="mt-1.5 inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#DBEAFE] text-[#1d4ed8] text-[11px] font-semibold uppercase tracking-[0.08em]">
            <FileDown className="size-3" /> Дижитал
          </span>
        )}
        {(c.showVariantName || c.dimension) && (
          <div className="mt-1 text-[13px] text-foreground/55 truncate">
            {[c.showVariantName ? c.variant?.name : null, c.dimension].filter(Boolean).join(' · ')}
          </div>
        )}
        {c.showDiscountedPrice && c.discountName && (
          <span className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.isReward ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
            {c.isReward ? <Gift className="size-3" strokeWidth={2.5} /> : <CirclePercent className="size-3" strokeWidth={2.5} />}
            <span className="line-clamp-1">{c.discountName}</span>
          </span>
        )}
        {c.loyaltyDeal && (
          <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 whitespace-nowrap">
            <Medal className="size-3" strokeWidth={2.5} /> Лоялти −{c.loyaltyDeal.percent}%
          </span>
        )}

        <div className="mt-3 flex items-center flex-wrap gap-3">
          {c.isReward ? (
            <div className="inline-flex items-center h-9 px-4 rounded-full border border-foreground/10 bg-white">
              <span className="min-w-5 text-center text-[13.5px] font-semibold text-foreground tabular-nums">{c.qty}</span>
            </div>
          ) : c.isOutOfStock ? (
            <span className="inline-flex items-center h-9 px-3.5 rounded-full bg-red-50 text-[13px] font-semibold text-red-600">Бараа дууссан</span>
          ) : (
            <div className="inline-flex items-center h-9 px-1 rounded-full border border-foreground/10 bg-white">
              <button onClick={c.handleRemoveOne} disabled={c.loading} className="w-7 h-7 inline-flex items-center justify-center rounded-full text-foreground hover:bg-foreground/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer">
                <Minus className="size-3.5" />
              </button>
              <span className="min-w-5 text-center text-[13.5px] font-semibold text-foreground px-2">{c.qty}</span>
              <button onClick={c.handleAddOne} disabled={c.loading} className="w-7 h-7 inline-flex items-center justify-center rounded-full text-foreground hover:bg-foreground/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer">
                <Plus className="size-3.5" />
              </button>
            </div>
          )}
          <button onClick={c.handleRemove} disabled={c.loading} className="inline-flex items-center gap-1.5 text-[13px] text-foreground/55 hover:text-foreground transition-colors disabled:opacity-40 cursor-pointer">
            <Trash2 className="size-3.5" /> Хасах
          </button>
        </div>
      </div>

      <div className="text-right shrink-0">
        <div className="text-[20px] sm:text-[22px] font-bold font-gilroy tracking-[-0.012em] text-foreground">
          {c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}
        </div>
        {c.qty > 1 && c.unitPrice > 0 && (
          <div className="mt-1 text-[12px] text-foreground/50">{formatPrice(c.unitPrice)} × {c.qty}</div>
        )}
        {c.showDiscountedPrice && c.originalPrice && (
          <div className="mt-1 text-[12px] line-through text-foreground/40">{formatPrice(c.qty * c.originalPrice)}</div>
        )}
      </div>
    </div>
  )
}
